import { Alert, Box } from '@mui/material';
import { NumberValue } from 'd3';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ConsolidatedWaveDataItem } from '@/api/historicalData';
import { useWaveData } from '@/api/historicalData/useWaveData';
import { usePreferredTimeZone } from '@/api/userAccount';
import waveSpreadIcon from '@/assets/WaveSpreadIcon.svg';
import { LineArea, LineChart, LineDataSlice, drawLineAreaChart } from '@/charts';
import { ChartTemplateProps, colorsets } from '@/charts/common';
import { addRenderersByProcessingSource } from '@/charts/helpers';
import { LineIcon } from '@/components/data/ColorIcon';
import { SensorIcon } from '@/components/data/SensorIcon';
import { ChartLineSelector, ChartLineSelectorProps } from '@/components/spotter/historicalDataTab/ChartLineSelector';
import {
    ChartDataDetails,
    ChartDataSelectors,
    ChartWithInfo,
    ProcessingSourceLegendItems,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineDirectionMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { LineChartDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/LineChartDatumDetail';
import { WaveDirectionDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/WaveDirectionDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useTrackChartView } from '@/hooks/useIsViewedTracking';
import { mod, wrapCompassDirections } from '@/utils';

export const compassHeadingYAxis: ChartTemplateProps['yAxisOptions'] = {
    tickFormat: (t: NumberValue) => mod(t.valueOf(), 360).toFixed(),
};

type LineConfig = LineArea<ConsolidatedWaveDataItem>;

const waveDirectionDatumDetailRenderer: LineConfig['datumDetailRenderer'] = (datum, line) => {
    return <WaveDirectionDatumDetail datum={datum} line={line as LineConfig} />;
};

export const MEAN_BULK_WAVE_DIRECTION_LINE = {
    id: 'mean-wave-direction-and-spread-bulk',
    label: 'Mean direction',
    sensorLabel: 'Bulk',
    selectorLabel: 'Bulk mean',
    valueKey: 'meanDirection' as const,
    yAreaKeyName: 'meanDirectionalSpread' as const,
    yAreaLabel: 'Mean spread',
    unit: 'deg',
    unitType: 'angle',
    datumDetailRenderer: waveDirectionDatumDetailRenderer,
    color: colorsets.blue,
} satisfies LineConfig & ChartLineSelectorProps['lines'][number];

export const PEAK_BULK_WAVE_DIRECTION_LINE = {
    id: 'peak-wave-direction-and-spread',
    label: 'Peak direction',
    sensorLabel: 'Bulk',
    selectorLabel: 'Bulk peak',
    valueKey: 'peakDirection' as const,
    yAreaKeyName: 'peakDirectionalSpread' as const,
    yAreaLabel: 'Peak spread',
    unit: 'deg',
    unitType: 'angle',
    datumDetailRenderer: waveDirectionDatumDetailRenderer,
    color: colorsets.yellow,
} satisfies LineConfig & ChartLineSelectorProps['lines'][number];

const BULK_WAVE_DIRECTION_LINES = [MEAN_BULK_WAVE_DIRECTION_LINE, PEAK_BULK_WAVE_DIRECTION_LINE];

const PARTITION_WAVE_DIRECTION_LINES = [
    {
        id: 'mean-wave-direction-and-spread-swell',
        label: 'Mean direction',
        sensorLabel: 'Swell',
        valueKey: 'meanDirectionSwell' as const,
        yAreaKeyName: 'meanDirectionalSpreadSwell' as const,
        yAreaLabel: 'Mean spread',
        unit: 'deg',
        unitType: 'angle',
        datumDetailRenderer: waveDirectionDatumDetailRenderer,
        color: colorsets.green,
    },
    {
        id: 'mean-wave-direction-and-spread-sea',
        label: 'Mean direction',
        sensorLabel: 'Sea',
        valueKey: 'meanDirectionSea' as const,
        yAreaKeyName: 'meanDirectionalSpreadSea' as const,
        yAreaLabel: 'Mean spread',
        unit: 'deg',
        unitType: 'angle',
        datumDetailRenderer: waveDirectionDatumDetailRenderer,
        color: colorsets.red,
    },
] satisfies (LineConfig & ChartLineSelectorProps['lines'][number])[];

export type WaveDirectionChartProps = {
    chartTitle: string;
    spotterId: string;
    lines: LineConfig[];
    onSelectData?: (data: LineDataSlice<ConsolidatedWaveDataItem, LineConfig> | null) => void;
    onSelectTimestamp?: (timestamp: string | null, timeline?: string[]) => void;
};

export const WaveDirectionChart = ({
    chartTitle,
    spotterId,
    onSelectData,
    onSelectTimestamp,
    lines,
}: WaveDirectionChartProps) => {
    const id = useId();
    const { dateRange } = useDateRangeContext();
    const { selectedTimestamp } = useTimelineNavigationContext();
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;
    const timeZone = usePreferredTimeZone();
    const chartRef = useRef<LineChart<ConsolidatedWaveDataItem, LineConfig> | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
    useTrackChartView({
        containerEl,
        spotterId: spotterId!,
        chartTitle,
    });
    const { data: waveData, isPending } = useWaveData({
        dateRange,
    });

    const containerRef = useRef<HTMLElement>(null);
    const containerId = `mean-wave-direction-and-spread-chart${id}`;
    const { width, height } = useElementSize(containerRef);

    const wrappedDirectionData = useMemo(() => {
        if (!waveData) {
            return [];
        }
        return wrapCompassDirections(waveData, [
            'meanDirection',
            'peakDirection',
            'meanDirectionSwell',
            'meanDirectionSea',
        ]);
    }, [waveData]);

    const drawChart = useCallback(drawLineAreaChart, []);

    const linesWithRenderers = useMemo(() => addRenderersByProcessingSource(lines, waveData), [lines, waveData]);

    useEffect(() => {
        if (isPending) {
            return;
        }
        if (!wrappedDirectionData.length) {
            return;
        }

        const newChart = drawChart<ConsolidatedWaveDataItem, LineConfig>({
            dataset: wrappedDirectionData,
            lines: linesWithRenderers,
            xAxisKey: 'timestamp',
            yAxisLabel: 'Wave direction',
            yAxisOptions: compassHeadingYAxis,
            chartWidth: width,
            chartHeight: height,
            containerId,
            dateRange,
            timeZone,
            onSelectData,
            onSelectTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        containerId,
        dateRange,
        timeZone,
        drawChart,
        height,
        isPending,
        linesWithRenderers,
        onSelectData,
        onSelectTimestamp,
        width,
        wrappedDirectionData,
    ]);

    useLayoutEffect(() => {
        if (!containerRef.current) {
            return;
        }
        setContainerEl(containerRef.current);
    }, [wrappedDirectionData?.length, dateRange]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    return <Box ref={containerRef} id={containerId} width={1} height={1} />;
};

export const WaveDirectionSection = () => {
    const { setSelectedTimestamp } = useTimelineNavigationContext();
    const chartTitle = 'Wave direction';
    const { spotterId } = useSpotterContext();
    const [activeLinesId, setActiveLinesId] = useState(
        [...BULK_WAVE_DIRECTION_LINES, ...PARTITION_WAVE_DIRECTION_LINES].map((line) => line.id)
    );
    const { dateRange } = useDateRangeContext();
    const [selectedData, setSelectedData] = useState<LineDataSlice<ConsolidatedWaveDataItem, LineConfig> | null>(null);

    const {
        data: waveData,
        isPending,
        isFetching,
        hasPartitionData,
    } = useWaveData({
        dateRange,
    });

    const lines = useMemo(
        () =>
            hasPartitionData
                ? [...BULK_WAVE_DIRECTION_LINES, ...PARTITION_WAVE_DIRECTION_LINES]
                : BULK_WAVE_DIRECTION_LINES,
        [hasPartitionData]
    );

    const activeLines = useMemo(() => lines.filter((line) => activeLinesId.includes(line.id)), [activeLinesId, lines]);

    return (
        <CollapsibleSection
            isFetching={isFetching}
            isPending={isPending}
            title={chartTitle}
            icon={<SensorIcon src={waveSpreadIcon} />}
        >
            {waveData?.length ? (
                <Box>
                    <ChartWithInfo
                        legend={<ProcessingSourceLegendItems data={waveData} />}
                        chart={
                            <WaveDirectionChart
                                onSelectTimestamp={setSelectedTimestamp}
                                lines={activeLines}
                                spotterId={spotterId!}
                                chartTitle={chartTitle}
                                onSelectData={setSelectedData}
                            />
                        }
                    >
                        <ChartDataSelectors>
                            <ChartLineSelector lines={lines} allLinesLabel="All data" onChange={setActiveLinesId} />
                        </ChartDataSelectors>
                        <ChartDataDetails>
                            {!selectedData && (
                                <LineChartDataSummary
                                    dateRange={dateRange}
                                    lines={activeLines}
                                    dataset={waveData}
                                    renderLineSummary={({ line: { color } }) => (
                                        <DataLineDirectionMean
                                            unit="deg"
                                            type="source"
                                            icon={<LineIcon dot color={color.line} />}
                                        />
                                    )}
                                />
                            )}
                            <DataDetailsByTimestamp
                                selectedData={selectedData}
                                datumRenderer={({ lines, datum }) => (
                                    <LineChartDatumDetail dataset={waveData} datum={datum} lines={lines} />
                                )}
                            />
                        </ChartDataDetails>
                    </ChartWithInfo>
                </Box>
            ) : (
                <Alert severity="info">No mean wave data for selected date range</Alert>
            )}
        </CollapsibleSection>
    );
};
